/**
 * Match kickoffs are UK football events, so they must always render in UK time
 * (Europe/London, i.e. GMT in winter / BST in summer) regardless of the
 * viewer's computer timezone. date-fns `format()` uses the machine's local
 * timezone, which shows the wrong time for anyone whose clock is not on UK time
 * (e.g. a UTC server/VM shows 4:50 where the UK is 5:50). These helpers pin the
 * display to UK time via the platform Intl API (no extra dependency).
 */
const UK_TZ = 'Europe/London';

function ukParts(iso: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormatPart[] {
  return new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, ...opts }).formatToParts(
    new Date(iso)
  );
}

function pick(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find(p => p.type === type)?.value ?? '';
}

/** e.g. "3:41pm" - UK time, matching the previous `h:mma` lowercase format. */
export function formatUkTime(iso: string): string {
  const p = ukParts(iso, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${pick(p, 'hour')}:${pick(p, 'minute')}${pick(p, 'dayPeriod')}`
    .toLowerCase()
    .replace(/\s/g, '');
}

/** e.g. "Tue 8 Jul" - UK time, matching the previous `EEE d MMM` format. */
export function formatUkDateShort(iso: string): string {
  const p = ukParts(iso, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${pick(p, 'weekday')} ${pick(p, 'day')} ${pick(p, 'month')}`;
}

/** e.g. "2026-07-08" - the UK calendar day, for grouping fixtures by date. */
export function ukDateKey(iso: string): string {
  const p = ukParts(iso, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${pick(p, 'year')}-${pick(p, 'month')}-${pick(p, 'day')}`;
}

/** e.g. "Wednesday 8 July 2026" - UK time, matching `EEEE d MMMM yyyy`. */
export function formatUkDateLong(iso: string): string {
  const p = ukParts(iso, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `${pick(p, 'weekday')} ${pick(p, 'day')} ${pick(p, 'month')} ${pick(p, 'year')}`;
}
